/**
 * Product card image hover cycling.
 *
 * On pointerenter (mouse only): cycles through every product image every 1.5 s.
 * On pointerleave: stops the interval and snaps back to the first (featured) image.
 *
 * Works with Horizon's slideshow-component custom element by calling its public
 * select() method — no hacks, no aria-hidden mutation by hand.
 */

const INTERVAL_MS = 1500;

class HoverCycle {
  /** @type {number|null} */ #timer = null;
  /** @type {Element[]} */   #slides = [];
  /** @type {number} */      #idx = 0;
  /** @type {Element|null} */ #slideshow = null;

  /** @param {Element} gallery */
  constructor(gallery) {
    gallery.addEventListener('pointerenter', this.#enter);
    gallery.addEventListener('pointerleave', this.#leave);
  }

  // ── private ──────────────────────────────────────────────────────────────

  /** @param {PointerEvent} e */
  #enter = (e) => {
    if (e.pointerType !== 'mouse') return;

    const gallery = /** @type {Element} */ (e.currentTarget);
    const sw = gallery.querySelector('slideshow-component');
    if (!sw || typeof sw.select !== 'function') return;

    // All slides that are part of the gallery (not hidden, not variant-only hidden slides)
    const slides = Array.from(sw.querySelectorAll('slideshow-slide:not([hidden])'));
    if (slides.length < 2) return;   // nothing to cycle

    // Eagerly load any lazy images so they're ready before we arrive at them
    for (const slide of slides) {
      const img = slide.querySelector('img[loading="lazy"]');
      if (img) img.loading = 'eager';
    }

    this.#slideshow = sw;
    this.#slides    = slides;
    this.#idx       = 0;

    this.#timer = setInterval(this.#advance, INTERVAL_MS);
  };

  #advance = () => {
    this.#idx = (this.#idx + 1) % this.#slides.length;
    const id = this.#slides[this.#idx]?.getAttribute('slide-id');
    if (id) this.#slideshow?.select({ id }, undefined, { animate: false });
  };

  /** @param {PointerEvent} e */
  #leave = (e) => {
    if (e.pointerType !== 'mouse') return;

    clearInterval(this.#timer);
    this.#timer = null;

    // Return to the first (featured) image
    const id = this.#slides[0]?.getAttribute('slide-id');
    if (id && this.#slideshow) {
      this.#slideshow.select({ id }, undefined, { animate: false });
    }

    this.#idx      = 0;
    this.#slides   = [];
    this.#slideshow = null;
  };
}

// ── bootstrap ──────────────────────────────────────────────────────────────

function attach(root = document) {
  root.querySelectorAll?.('.card-gallery[data-hover-cycle]').forEach((el) => {
    if (!el._hoverCycle) el._hoverCycle = new HoverCycle(el);
  });
}

// Initial page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => attach());
} else {
  attach();
}

// Re-attach after AJAX navigation / collection filter morphs
new MutationObserver((mutations) => {
  for (const m of mutations) {
    for (const node of m.addedNodes) {
      if (node.nodeType === 1) attach(/** @type {Element} */ (node));
    }
  }
}).observe(document.body, { childList: true, subtree: true });
